import {
  getEditorDefaults,
  PinturaDefaultImageWriterResult,
  PinturaReadState,
  PinturaWriteState
} from '@pqina/pintura'
import '@pqina/pintura/pintura.css'
import { PinturaEditor } from '@pqina/react-pintura'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import styles from './ImageEditor.pcss'
import {
  configureStickerWillRenderShapePresetToolbar,
  createMarkupEditorConfig,
  defaultVisibleStickers,
  markupEditorShapeStyleControlsDefault,
  markupEditorToolbarDefault,
  markupEditorToolStylesDefault
} from './pintura.config'

interface ImageEditorProps {
  imageUrl: string
  className?: string
  hideSaveButton?: boolean
  onExport?: (file: File) => void
  onLoadError?: (error: PinturaReadState) => void
  onProcessError?: (error: PinturaWriteState) => void
}

export const ImageEditor = forwardRef<PinturaEditor, ImageEditorProps>((
  { imageUrl, className, hideSaveButton, onExport, onLoadError, onProcessError }: ImageEditorProps,
  ref: React.ForwardedRef<PinturaEditor>
) => {
  // EXPL: When no image src is set, Editor will show blank space with 'Waiting for image' text.
  // Set empty canvas as src to imitialize the editor with empty image
  const canvas = useMemo(() => {
    const image = document.createElement('canvas')
    image.width = 1024
    image.height = 768
    const imageContext = image.getContext('2d')
    if (imageContext) {
      imageContext.fillStyle = 'white'
      imageContext.fillRect(0, 0, image.width, image.height)
    }
    return image
  }, [])

  useEffect(() => {
    // When image loads, get its dimensions
    const img = new Image()
    img.onload = () => {
      const editor = innerRef.current?.editor
      if (editor) {
        const markupConfig = createMarkupEditorConfig(img.width, img.height)
        editor.markupEditorShapeStyleControls = markupConfig
      }
    }
    img.src = imageUrl
  }, [imageUrl])

  const defaultConfig = getEditorDefaults()
  const utils = (defaultConfig.utils || []).filter(u => u !== 'decorate')

  const innerRef = useRef<PinturaEditor>(null)
  // Expose underlying editor instance to parent
  useImperativeHandle(ref, () => innerRef.current as any, [])

  const onProcess = (res: PinturaDefaultImageWriterResult) => {
    if (onExport) onExport(res.dest)
  }

  return (
    <div className={`${styles.editor} ${className || ''}`} data-testid="image-editor">
      <PinturaEditor
        ref={innerRef}
        {...defaultConfig}
        utils={utils}
        src={imageUrl || canvas}
        willRenderToolbar={(toolbar, _env, _redraw) => hideSaveButton ? toolbar.slice(0, 2) : toolbar}
        annotateActiveTool="text"
        fixScrollDirection
        stickers={defaultVisibleStickers}
        stickerWillRenderShapePresetToolbar={configureStickerWillRenderShapePresetToolbar('[data-testid="image-editor"]')}
        markupEditorToolbar={markupEditorToolbarDefault}
        markupEditorToolStyles={markupEditorToolStylesDefault}
        markupEditorShapeStyleControls={markupEditorShapeStyleControlsDefault}
        markupEditorSnapThreshold={5}
        markupEditorSnapToContext={true}
        annotateSnapToContext={true}
        // cropSelectPresetFilter={'portrait'}
        cropSelectPresetOptions={[
          [undefined, 'Custom'],
          /* eslint-disable no-magic-numbers */
          [1 / 1, '(1:1) Instagram Post'],
          [4 / 5, '(4:5) Instagram Portrait'],
          [9 / 16, '(9:16) Instagram Story'],
          [16 / 9, '(16:9) YouTube Thumbnail'],
          [3 / 2, '(3:2) Classic Photo'],
          [2 / 3, '(2:3) Pinterest Pin'],
          [1.91 / 1, '(1.91:1) Facebook Ad'],
          [2 / 1, '(2:1) Twitter Header'],
          [205 / 78, '(205:78) Facebook Cover']
          /* eslint-enable no-magic-numbers */
        ]}
        onProcess={onProcess}
        onLoaderror={onLoadError}
        onProcesserror={onProcessError}
      />
    </div>
  )
})

export default ImageEditor
